import React, { FC, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Input, PhotoUploader, PrimaryButton } from 'common/components';
import {
  Wrapper,
  ButtonsWrapper,
  FieldsSection,
  StyledDivider,
} from './styles';
import { useUserInfoForm } from '../../hooks/useUserInfoForm';
import { useAppDispatch, useAppSelector } from 'common/hooks/redux';
import { uploadedPhoto } from 'applicaiton/store/reducers/Photo/PhotoSlice';
import compareObjectsChanges from 'common/helpers/compareObjectsChanges';
import { editAdmin } from 'applicaiton/store/reducers/Admins/ActionCreators';
import SuccessModal from '../SuccessModal';
import { setIsAdminUpdated } from 'applicaiton/store/reducers/Admins/AdminsSlice';

const ProfileInfo: FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const { register, errors, handleSubmit, watch, setValue } = useUserInfoForm({
    requiredError: t('errors.required'),
  });

  const { uploadedPhotos } = useAppSelector((state) => state.photos);
  const { currentUser, isUpdated } = useAppSelector((state) => state.admins);

  const isStateChanged = useMemo(() => {
    const hasChanges = compareObjectsChanges(
      {
        firstName: currentUser?.firstName,
        lastName: currentUser?.lastName,
        email: currentUser?.email,
      },
      {
        firstName: watch('firstName'),
        lastName: watch('lastName'),
        email: watch('email'),
      },
    );

    return hasChanges || !!uploadedPhotos.length;
  }, [
    currentUser,
    watch('firstName'),
    watch('lastName'),
    watch('email'),
    uploadedPhotos,
  ]);

  useEffect(() => {
    if (currentUser) {
      setValue('firstName', currentUser.firstName);
      setValue('lastName', currentUser.lastName);
      setValue('email', currentUser.email);

      if (currentUser?.photo) {
        const photo = {
          key: currentUser?.photo.key,
          photoUrl: currentUser.photo.photoUrl,
          thumbnailUrl: currentUser.photo.thumbnailUrl,
        };
        uploadedPhoto([photo]);
      }
    }
  }, [currentUser]);

  const onSubmit = async () => {
    dispatch(
      editAdmin({
        id: currentUser?.id!,
        data: {
          firstName: watch('firstName'),
          lastName: watch('lastName'),
          ...(uploadedPhotos.length
            ? {
                photo: uploadedPhotos[0],
                ...(currentUser?.photo?.id
                  ? { photoToDelete: currentUser?.photo?.id }
                  : {}),
              }
            : {}),
        },
      }),
    ).then(() => {
      dispatch(uploadedPhoto([]));
    });
  };

  const closeSuccessModal = () => {
    dispatch(setIsAdminUpdated());
  };

  return (
    <Wrapper>
      <FieldsSection>
        <Input
          id="email"
          label={t('email') || ''}
          type="email"
          placeholder={t('email_placeholder') || ''}
          register={register}
          errorMessage={errors.email?.message}
          disabled={true}
        />
        <Input
          label={t('personal_information.first_name') || ''}
          id={'firstName'}
          type="text"
          register={register}
          errorMessage={errors?.firstName?.message}
          placeholder={t('personal_information.first_name_placeholder') || ''}
        />
        <Input
          label={t('personal_information.last_name') || ''}
          id={'lastName'}
          type="text"
          register={register}
          errorMessage={errors?.lastName?.message}
          placeholder={t('personal_information.last_name_placeholder') || ''}
        />
        <PhotoUploader
          defaultPhoto={currentUser?.photo}
          label={t('upload_photo_optional') || ''}
        />
      </FieldsSection>
      <StyledDivider />
      <ButtonsWrapper>
        <PrimaryButton
          type={'button'}
          onClick={handleSubmit(onSubmit)}
          disabled={!isStateChanged}
        >
          {t('save')}
        </PrimaryButton>
      </ButtonsWrapper>
      {isUpdated && (
        <SuccessModal
          typeName={`${currentUser?.firstName} ${currentUser?.lastName}`}
          onClose={closeSuccessModal}
        />
      )}
    </Wrapper>
  );
};

export default ProfileInfo;
